"use client";
import { Track } from "@/src/lib/tracking";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Member/CurrentPlan/subscriptionRetention/CancelFeedbackSurveyModalContent";

import { FC, useEffect, useMemo, useState } from "react";
import { Icon, TextAreaField } from "@ospweb/sdk-ui";
import { cn } from "utils";

const I18n = I18nUtil.createScoped(Translations);

export interface CancelFeedbackSurveyModalContentProps {
  onClose: () => void;
  /** 提交当前选项与备注（字数由组件内限制 500） */
  onSubmit?: () => void;
}

/**
 * 取消订阅反馈弹窗（Figma 82684:223765，不含 Home Indicator）
 */
export const CancelFeedbackSurveyModalContent: FC<
  CancelFeedbackSurveyModalContentProps
> = ({ onClose, onSubmit }) => {
  const [selected, setSelected] = useState<string>("optExpensive");
  const [message, setMessage] = useState("");
  const maxLen = 500;

  const handleSubmit = () => {
    onSubmit?.();
  };

  useEffect(() => {
    Track.view("member_subscription_cancel_feedback_survey_modal_view");
  }, []);

  const OPTIONS = useMemo(() => {
    return [
      {
        key: "optExpensive",
        label: I18n.t("Too expensive"),
      },
      {
        key: "optNotUsing",
        label: I18n.t("I'm not using it enough"),
      },
      {
        key: "optMissing",
        label: I18n.t("Missing features I need"),
      },
      {
        key: "optAlternative",
        label: I18n.t("I found a better alternative"),
      },
      {
        key: "optTechnical",
        label: I18n.t("Technical issues / bugs"),
      },
      {
        key: "optTesting",
        label: I18n.t("Just testing, not ready to pay"),
      },
      {
        key: "optOther",
        label: I18n.t("Other"),
      },
    ];
  }, []);

  return (
    <div className="relative flex w-full flex-col px-6 pb-4 pt-6 text-content_inverse_primary">
      <Icon
        data-openreplay-label="member_cancelfeedbacksurvey_close"
        name="close"
        size={24}
        className="button_icon absolute right-3 top-3.5 z-10 cursor-pointer text-content_primary"
        onClick={onClose}
      />
      <div className="flex flex-col gap-6 pt-2 pb-safe">
        <div className="flex flex-col gap-2 text-center">
          <p className="headline_regular_s text-content_primary">
            {I18n.t("We’re sorry to see you go")}
          </p>
          <p className="body_m text-content_secondary">
            {I18n.t("Help Ed improve. Why are you leaving?")}
          </p>
        </div>

        <div
          className="flex flex-col gap-2"
          role="radiogroup"
          aria-label={I18n.t("title")}
        >
          {OPTIONS.map(({ key, label }) => {
            const isOn = selected === key;
            return (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={isOn}
                data-openreplay-label="member_cancelfeedbacksurvey_click"
                className="flex h-10 w-full items-center justify-between gap-3 py-2 text-left"
                onClick={() => {
                  setSelected(key);
                  setMessage(key === "optOther" ? "" : label);
                }}
              >
                <span className="body_medium_m text-content_primary">
                  {label}
                </span>
                <span
                  className={cn(
                    "flex-center size-4 shrink-0 rounded-full bg-background_white_overlay10",
                    {
                      "bg-background_brand_overlay100 before:block before:content-[''] before:size-1.5 before:rounded-full before:bg-content_inverse_primary":
                        isOn,
                    },
                  )}
                />
              </button>
            );
          })}
          <div
            className={cn("hidden w-full flex-col gap-2", {
              flex: selected === "optOther",
            })}
          >
            <TextAreaField
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={I18n.t(
                "Ed is listening. Please share your thoughts...",
              )}
              maxLength={maxLen}
              borderVisible={true}
              autoSize={{ minRows: 5, maxRows: 8 }}
              containerClassName="min-h-[115px] w-full bg-background_white_overlay5"
              placeholderClass="top-2 left-3 animate-none bg-none [-webkit-text-fill-color:unset] text-content_quaternary"
            />
            <p className="body_s text-right text-content_tertiary">
              {message.length}/{maxLen}
            </p>
          </div>
        </div>

        <button
          type="button"
          data-openreplay-label="member_cancelfeedbacksurvey_submit"
          disabled={selected === "optOther" && message.length === 0}
          className="button_m md_button primary_button"
          onClick={handleSubmit}
        >
          {I18n.t("submit")}
        </button>
      </div>
    </div>
  );
};
